import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Grid, IconButton, Paper, List, ListItem, ListItemText, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { fetchPublicContentModule } from '../../services/publicContentApi';
import type { HomepageEditalItem } from '../../types/content';

const tabs: { id: 'sobre' | 'funcionamento' | 'melhorias' | 'relatorios' | 'contato'; label: string }[] = [
  { id: 'sobre', label: 'Sobre a CPA' },
  { id: 'funcionamento', label: 'Composição' },
  { id: 'melhorias', label: 'Melhorias' },
  { id: 'relatorios', label: 'Avaliações Institucionais' },
  { id: 'contato', label: 'Contato' },
];

const fallbackDocuments: HomepageEditalItem[] = [
  { id: 'cpa-composicao-1', title: 'Portaria 2024 CPA Nomeação', tag: 'Composicao', href: '/assets/cpa/2024.1_Portaria-2024-CPA-Nomeacao.pdf', order: 1, isActive: true },
  { id: 'cpa-composicao-2', title: 'Resolução Nº 01 da CPA (Fatese 2022)', tag: 'Composicao', href: '/assets/cpa/Resolucao-No-01-da-CPA-FATESE-2022.pdf', order: 2, isActive: true },
  { id: 'cpa-relatorio-2025', title: 'Relatório Parcial da CPA 2025', tag: 'Relatorios', href: '/assets/cpa/relatorios/RELATORIO-PARCIAL-CPA-2025.1-1.pdf', order: 3, isActive: true },
  { id: 'cpa-relatorio-2024', title: 'Relatório Final da CPA 2024', tag: 'Relatorios', href: '/assets/cpa/relatorios/RELATORIO-FINAL-DA-CPA-2024.pdf', order: 4, isActive: true },
  { id: 'cpa-relatorio-2023', title: 'Relatório Final da CPA 2023', tag: 'Relatorios', href: '/assets/cpa/relatorios/RELATORIO-FINAL-DA-CPA-2023_compressed.pdf', order: 5, isActive: true },
  { id: 'cpa-relatorio-2022', title: 'Relatório Final da CPA 2022', tag: 'Relatorios', href: '/assets/cpa/relatorios/RELATORIO-FINAL-DA-CPA-2022_compressed.pdf', order: 6, isActive: true },
  { id: 'cpa-relatorio-2021', title: 'Relatório Final da CPA 2021', tag: 'Relatorios', href: '/assets/cpa/relatorios/RELATORIO-FINAL-DA-CPA-2021-FATESE.pdf', order: 7, isActive: true },
  { id: 'cpa-relatorio-2019', title: 'Relatório Final da CPA 2019', tag: 'Relatorios', href: '/assets/cpa/relatorios/RELATORIO-FINAL-DA-CPA-2019-FATESE.pdf', order: 8, isActive: true },
  { id: 'cpa-relatorio-2017', title: 'Relatório Final da CPA 2017', tag: 'Relatorios', href: '/assets/cpa/relatorios/RELATORIO-FINAL-DA-CPA-2017-FATESE.pdf', order: 9, isActive: true },
];

function CpaDocumentList({ items }: { items: HomepageEditalItem[] }) {
  if (!items.length) return <Typography color="text.secondary">Nenhum documento publicado no momento.</Typography>;
  return (
    <List>
      {items.map((item) => (
        <ListItem key={item.id} secondaryAction={<Button variant="contained" component="a" href={item.href} target="_blank" rel="noopener noreferrer">Baixar PDF</Button>}>
          <ListItemText primary={item.title} />
        </ListItem>
      ))}
    </List>
  );
}

const CPA: React.FC = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<'sobre' | 'funcionamento' | 'melhorias' | 'relatorios' | 'contato'>('sobre');
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [documents, setDocuments] = useState<HomepageEditalItem[]>(fallbackDocuments);

  useEffect(() => {
    let mounted = true;
    void fetchPublicContentModule<HomepageEditalItem>('cpaDocuments')
      .then((response) => { if (mounted) setDocuments(response.items || []); })
      .catch(() => undefined);
    return () => { mounted = false; };
  }, []);

  const underline = {
    content: '""',
    position: 'absolute' as const,
    left: '50%',
    transform: 'translateX(-50%)',
    bottom: 0,
    width: 120,
    height: 4,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.secondary.main,
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'sobre':
        return (
          <>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
              Sobre a CPA
            </Typography>
            <Typography paragraph textAlign="justify" sx={{ lineHeight: 1.8 }}>
              Na Faculdade de Tecnologia Senac Amazonas, a voz da nossa comunidade acadêmica é a força motriz da evolução institucional. Por meio dos resultados da Avaliação Institucional, conduzida pela Comissão Própria de Avaliação (CPA), identificamos as demandas e priorizamos melhorias contínuas em nossa estrutura, processos acadêmicos e serviços.
            </Typography>
            <Typography paragraph textAlign="justify" sx={{ lineHeight: 1.8 }}>
              Neste espaço, você confere as ações já implementadas – todas realizadas com transparência e compromisso com a excelência educacional que nos move. Aqui, cada feedback se torna um passo concreto em direção a uma experiência acadêmica ainda mais qualificada e inclusiva.
            </Typography>
          </>
        );

      case 'funcionamento':
        return (
          <>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
              Composição da CPA
            </Typography>
            <CpaDocumentList items={documents.filter((item) => item.tag === 'Composicao')} />
          </>
        );

      case 'melhorias':
        return (
          <>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
              Quais os benefícios da CPA para a gestão acadêmica?
            </Typography>
            <Box component="ul" sx={{ pl: 3, lineHeight: 1.8 }}>
              <li><strong>Identificar Fragilidades:</strong> Docentes, Discentes e Colaboradores estão intimamente ligados à rotina da instituição, de forma que eles têm mais clareza e objetividade para apontar as vulnerabilidades. Excesso de burocracia, volume de trabalho, didática inadequada e diversos outros aspectos são passíveis de mensuração e, consequentemente, impactam no avanço da metodologia institucional. Por oferecer essas perspectivas diversificadas, a heterogeneidade dos membros da CPA é essencial.</li>
              <li><strong>Criar Novas Políticas Educacionais:</strong> Muitos modelos novos de metodologias pedagógicas estão surgindo, como aumento da interdisciplinaridade ou a mudança na perspectiva do papel do docente sobre a construção do conhecimento e os métodos de ensino aprendizagem. O relatório da comissão costuma evidenciar lacunas que, de outra forma, seriam ignoradas pela gestão acadêmica. Por meio desse levantamento de dados, a gestão consegue direcionar esforços para revisar suas políticas e atualizar os modelos de ensino, o que mantém a instituição relevante no meio acadêmico. Inclusive, é extremamente importante apostar no planejamento estratégico e na integração com outras IES como forma de inovação.</li>
              <li><strong>Avaliar Novos Investimentos:</strong> Na busca pelo equilíbrio e a sustentabilidade financeira a CPA auxilia no estudo da viabilidade de novos investimentos direcionados ao investimento em tecnologia, Modernizar funções, desburocratizar processos, ter mais rastreabilidade e coletar dados em tempo real são aplicações possíveis por meio de sistemas informatizados. Ampliação e modernização no programa de acessibilidade e inclusão social como diferencial.</li>
              <li><strong>Responsabilidade Socioambiental:</strong> Os projetos desenvolvidos pela FATESE, direcionados ao público externo, demonstram o compromisso da instituição com o desenvolvimento social e a responsabilidade com meio ambiente. Esse comprometimento pode ser mensurado por meio de ações tomadas para conscientizar a população e promover a inclusão, a cidadania e a sustentabilidade.</li>
            </Box>
          </>
        );

      case 'relatorios':
        return (
          <>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
              Relatórios
            </Typography>
            <CpaDocumentList items={documents.filter((item) => item.tag === 'Relatorios')} />
          </>
        );

      case 'contato':
        return (
          <>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
              Contato da CPA
            </Typography>
            <Typography>Email: cpa@am.senac.br</Typography>
            <Typography>Telefone: (92) 3199-9956</Typography>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 8 } }}>
      {/* Título principal com traço laranja */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 700,
            letterSpacing: 0.2,
            display: 'inline-block',
            pb: 1.5,
            position: 'relative',
            '&::after': underline,
          }}
        >
          CPA - Comissão Própria de Avaliação
        </Typography>
      </Box>

      {/* Botão hamburguer (mobile) */}
      <Box display={{ xs: 'block', md: 'none' }} textAlign="right" mb={2}>
        <IconButton onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
        </IconButton>
      </Box>

      <Grid container spacing={4}>
        <Grid
          item
          xs={12}
          md={3}
          display={{ xs: isMobileMenuOpen ? 'block' : 'none', md: 'block' }}
        >
          <Box display="flex" flexDirection="column" gap={2}>
            {tabs.map((tab) => (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'contained' : 'outlined'}
                onClick={() => {
                  setActiveTab(tab.id);
                  setMobileMenuOpen(false);
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                {tab.label}
              </Button>
            ))}
          </Box>
        </Grid>

        <Grid item xs={12} md={9}>
          <Paper sx={{ p: { xs: 2, md: 4 } }}>
            {renderContent()}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CPA;
